import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Download, Archive, FileText, Lock, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function bytes(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function PublicDownloadLink() {
  const { token } = useParams();
  const [password, setPassword] = useState("");
  const [manifest, setManifest] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (!password) { toast.error("Enter the password"); return; }
    setVerifying(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/public/download-link/${token}/verify`, { password });
      setManifest(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setVerifying(false); }
  };

  const downloadOne = async (doc) => {
    setBusy(true);
    try {
      const res = await axios.get(`${BACKEND}/api/public/download-link/${token}/file/${doc.id}`, {
        params: { password }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.original_filename || `${doc.doc_type || "file"}.bin`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setBusy(false); }
  };

  const downloadZip = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${BACKEND}/api/public/download-link/${token}/zip`, {
        params: { password }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `documents.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ZIP download failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen py-16 bg-[#F4F4F5]">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white border border-[#E4E4E7] rounded-2xl p-8">
          <div className="flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-[#0052FF]">
            <ShieldCheck size={14} /> Secure Document Download
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-[#06152D]">Loan Junction</h1>
          <p className="mt-1 text-sm text-[#52525B]">Enter the password shared with you to access your documents.</p>

          {!manifest ? (
            <form onSubmit={verify} className="mt-6 space-y-3" data-testid="download-verify-form">
              <div>
                <Label className="inline-flex items-center gap-1"><Lock size={12} /> Password</Label>
                <Input
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  data-testid="download-password"
                />
              </div>
              <button
                type="submit"
                disabled={verifying}
                className="w-full bg-[#0052FF] hover:bg-[#003FCC] disabled:opacity-60 text-white font-semibold px-4 py-3 rounded-full"
                data-testid="download-verify-btn"
              >
                {verifying ? "Verifying…" : "Unlock Documents"}
              </button>
              <p className="text-xs text-[#94A3B8]">Password protected. Link expires and may have a limited number of downloads.</p>
            </form>
          ) : (
            <div className="mt-6" data-testid="download-manifest">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold text-[#06152D]">{manifest.documents?.length || 0} document{manifest.documents?.length === 1 ? "" : "s"}</div>
                  <div className="text-xs text-[#52525B] inline-flex items-center gap-1 mt-0.5">
                    <Clock size={12} /> Expires {new Date(manifest.expires_at).toLocaleString("en-IN")}
                  </div>
                </div>
                {(manifest.documents?.length || 0) > 1 && (
                  <button
                    onClick={downloadZip}
                    disabled={busy}
                    className="bg-[#06152D] hover:bg-[#0b2350] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5"
                    data-testid="download-all-zip"
                  >
                    <Archive size={14} /> Download All as ZIP
                  </button>
                )}
              </div>
              {manifest.note && (
                <div className="mt-3 text-xs bg-[#F4F4F5] border border-[#E4E4E7] rounded-lg p-3 text-[#52525B]">
                  <span className="font-semibold text-[#06152D]">Note:</span> {manifest.note}
                </div>
              )}
              <div className="mt-4 border border-[#E4E4E7] rounded-xl divide-y divide-[#E4E4E7]" data-testid="download-file-list">
                {(manifest.documents || []).map((d) => (
                  <div key={d.id} className="p-3 flex items-center gap-3" data-testid={`file-row-${d.id}`}>
                    <FileText size={18} className="text-[#0052FF] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#06152D] truncate">{d.original_filename || d.doc_type || d.id}</div>
                      <div className="text-[10px] text-[#52525B]">{d.doc_type || "document"} · {bytes(d.size)}</div>
                    </div>
                    <button
                      onClick={() => downloadOne(d)}
                      disabled={busy}
                      className="bg-[#0052FF] hover:bg-[#003FCC] disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1"
                      data-testid={`download-file-${d.id}`}
                    >
                      <Download size={12} /> Download
                    </button>
                  </div>
                ))}
                {(manifest.documents || []).length === 0 && (
                  <div className="p-6 text-center text-[#52525B] text-sm">No documents.</div>
                )}
              </div>
              <p className="mt-4 text-[10px] text-[#94A3B8] text-center">This link exposes only the documents shared with you — no other customer information is accessible.</p>
            </div>
          )}
        </div>
        <p className="text-center text-xs text-[#52525B] mt-4">Loan Junction Finance Services · Siddipet, Telangana</p>
      </div>
    </section>
  );
}
